import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FaTicketAlt, FaLock, FaUser, FaEnvelope, FaPhone, FaStar, FaPaypal } from "react-icons/fa";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import TierSelector from "../../components/Ticketing/TierSelector";
import OrderSummary from "../../components/Ticketing/OrderSummary";
import PromoCodeInput from "../../components/Ticketing/PromoCodeInput";
import PayPalCheckoutButton from "../../components/Ticketing/PayPalCheckoutButton";
import LoadingPage from "../../components/Loading/LoadingPage";
import { useAuth } from "../../contexts/AuthContext";
import {
  getEventTiers,
  getEventPacks,
  getEventPromotions,
  getEventAvailability,
  createReservation,
  getMyTickets,
  formatPrice,
  getMemberEffectivePrice,
  calculateOrderTax,
} from "../../services/ticketingService";
import { getCustomerPaymentMethod } from "../../services/auctionService";
import { ttqTrack } from "../../utils/tiktok";
import { fbqTrack } from "../../utils/meta";

import "./EventTicketPage.css";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const IS_DEV = process.env.NODE_ENV !== "production";

/**
 * Event ticketing page — select tiers, quantity, and checkout.
 * Route: /events/:eventSlug/tickets
 *
 * Supports:
 *  - Loyalty customer checkout (using customer_token)
 *  - Guest checkout (enter email/name)
 *  - Free ticket immediate confirmation
 *  - Paid ticket → Stripe redirect
 *  - Member-only tiers (hidden from non-members)
 *  - Early Bird / Door price tier badges
 *  - Buy X get Y free promo display
 */
export default function EventTicketPage() {
  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customerToken, email: authEmail, viewMode, triggerLogin } = useAuth();

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const [event, setEvent] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [packs, setPacks] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [packQuantities, setPackQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // { reservationId, paypalOrderId } — shown after "Pay with Venmo" click
  // const [pendingVenmo, setPendingVenmo] = useState(null);

  // Checkout mode: "account" | "guest" — auto-set to account when logged in
  const [checkoutMode, setCheckoutMode] = useState(viewMode === "resident" ? "account" : null);

  // If auth resolves after mount (token loaded async), auto-switch to account mode
  useEffect(() => {
    if (viewMode === "resident" && customerToken) {
      setCheckoutMode((prev) => prev === "guest" ? prev : "account");
    }
  }, [viewMode, customerToken]);

  // When user signs out: reset checkout mode and migrate member-tier quantities back to regular tier
  useEffect(() => {
    if (viewMode !== "resident") {
      setCheckoutMode((prev) => prev === "guest" ? prev : null);
      setQuantities((prev) => {
        const memberTierItem = tiers.find((t) => t.is_member_only && !t.member_locked);
        const memberIds = new Set(tiers.filter((t) => t.is_member_only).map((t) => t.tier_id));
        if (memberIds.size === 0) return prev;
        const next = { ...prev };
        // Migrate member-tier qty back to the active regular tier before deleting
        if (memberTierItem) {
          const activeRegular = tiers.find((t) => !t.is_member_only && t.sale_status === "on_sale");
          if (activeRegular) {
            const qty = next[memberTierItem.tier_id] || 0;
            if (qty > 0) next[activeRegular.tier_id] = (next[activeRegular.tier_id] || 0) + qty;
          }
        }
        memberIds.forEach((id) => delete next[id]);
        return next;
      });
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guest form
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  // Whether customer is a member (set after token decode)
  const [isMember, setIsMember] = useState(false);

  // Applied promo code: { code, discount_cents, discount_type, discount_value, description } | null
  const [appliedPromo, setAppliedPromo] = useState(null);

  // Pending PayPal reservation waiting for buyer approval (PayPal UI disabled for now)
  const [pendingPayPal, setPendingPayPal] = useState(null);

  // Event-level capacity remaining (max_capacity - all tickets sold across tiers + packs)
  const [eventCapacityRemaining, setEventCapacityRemaining] = useState(null);

  // Tickets the logged-in user already has for this event
  const [existingTickets, setExistingTickets] = useState([]);

  // Fetch event details
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/loyalty/events/${eventSlug}`);
        if (!res.ok) throw new Error("Event not found");
        const data = await res.json();
        if (mounted) setEvent(data);
      } catch {
        if (mounted) setError("Could not load event details.");
      }
    })();
    return () => { mounted = false; };
  }, [eventSlug, apiUrl]);

  // Fetch tiers, promotions, packs, and event-level availability once event is loaded
  useEffect(() => {
    if (!event?.event_id) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [tiersData, promosData, packsData, availData] = await Promise.all([
          getEventTiers(event.event_id, customerToken),
          getEventPromotions(event.event_id),
          getEventPacks(event.event_id),
          getEventAvailability(event.event_id),
        ]);
        if (mounted) {
          setTiers(tiersData);
          setPromotions(promosData);
          setPacks(packsData);
          if (availData?.tickets_remaining !== null && availData?.tickets_remaining !== undefined) {
            setEventCapacityRemaining(availData.tickets_remaining);
          }
          setLoading(false);

          // When user logs in as a member, migrate any existing regular-tier quantities
          // to the member tier ID (TierSelector uses memberTier.tier_id as the cart key).
          const newMemberTier = tiersData.find((t) => t.is_member_only && !t.member_locked);
          if (newMemberTier) {
            setQuantities((prev) => {
              const regularIds = tiersData
                .filter((t) => !t.is_member_only)
                .map((t) => t.tier_id);
              const regularTotal = regularIds.reduce((sum, id) => sum + (prev[id] || 0), 0);
              if (regularTotal === 0) return prev;
              const next = { ...prev };
              regularIds.forEach((id) => delete next[id]);
              next[newMemberTier.tier_id] = (next[newMemberTier.tier_id] || 0) + regularTotal;
              return next;
            });
          }
        }
      } catch {
        if (mounted) {
          setError("Could not load ticket options.");
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [event?.event_id, customerToken]);

  // isMember is set server-side — member-only tiers are already filtered out of the tiers response.
  // We detect it here by checking if any returned tier has is_member_only=true (only visible to members).
  useEffect(() => {
    if (tiers.length > 0) {
      // member_locked=false means this user IS a member (server confirmed)
      setIsMember(tiers.some((t) => t.is_member_only && !t.member_locked));
    }
  }, [tiers]);

  // Fetch existing tickets for this event when logged in
  useEffect(() => {
    if (!customerToken || !event?.event_id) return;
    let mounted = true;
    (async () => {
      try {
        const all = await getMyTickets(customerToken);
        if (mounted) {
          setExistingTickets(
            (Array.isArray(all) ? all : []).filter(
              (t) => t.event_id === event.event_id && (t.status === "valid" || t.status === "used")
            )
          );
        }
      } catch {
        // Non-critical — silently ignore
      }
    })();
    return () => { mounted = false; };
  }, [customerToken, event?.event_id]);

  const handleQuantityChange = (tierId, qty) => {
    setQuantities((prev) => ({ ...prev, [tierId]: qty }));
  };

  const handlePackQuantityChange = (packId, qty) => {
    setPackQuantities((prev) => ({ ...prev, [packId]: qty }));
  };

  const totalTierTickets = Object.values(quantities).reduce((s, q) => s + q, 0);
  const totalPackTickets = packs.reduce((s, p) => s + (packQuantities[p.pack_id] || 0) * p.quantity, 0);
  const totalTickets = totalTierTickets + totalPackTickets;

  const tierSubtotalCents = tiers.reduce(
    (sum, t) => sum + (quantities[t.tier_id] || 0) * getMemberEffectivePrice(t, tiers),
    0
  );
  const packSubtotalCents = packs.reduce(
    (sum, p) => sum + (packQuantities[p.pack_id] || 0) * p.pack_price_cents,
    0
  );
  const subtotalCents = tierSubtotalCents + packSubtotalCents;

  const discountCents = appliedPromo?.discount_cents || 0;
  const discountRatio = (discountCents > 0 && subtotalCents > 0)
    ? Math.min(1, discountCents / subtotalCents)
    : 0;
  const tierTaxCents = calculateOrderTax(tiers, quantities, discountCents, subtotalCents);
  const packTaxCents = packs.reduce((sum, p) => {
    const qty = packQuantities[p.pack_id] || 0;
    if (qty === 0) return sum;
    const taxableAmount = Math.round(p.pack_price_cents * qty * (1 - discountRatio));
    return sum + Math.round(taxableAmount * ((p.tax_percent || 0) / 100));
  }, 0);
  const taxCents = tierTaxCents + packTaxCents;
  const totalCents = Math.max(0, subtotalCents - discountCents + taxCents);
  const isFreeOrder = totalCents === 0;

  // Clear promo when cart becomes empty
  useEffect(() => {
    if (totalTickets === 0 && appliedPromo) setAppliedPromo(null);
  }, [totalTickets]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedItems = tiers
    .filter((t) => (quantities[t.tier_id] || 0) > 0)
    .map((t) => ({ tier_id: t.tier_id, quantity: quantities[t.tier_id] }));

  const selectedPackItems = packs
    .filter((p) => (packQuantities[p.pack_id] || 0) > 0)
    .map((p) => ({ pack_id: p.pack_id, quantity: packQuantities[p.pack_id] }));

  const exceedsEventCapacity =
    eventCapacityRemaining !== null && totalTickets > eventCapacityRemaining;

  const canCheckout =
    (selectedItems.length > 0 || selectedPackItems.length > 0) &&
    !exceedsEventCapacity &&
    (checkoutMode === "account" ||
      (checkoutMode === "guest" && guestName.trim() && guestEmail.trim()));

  const buildPayload = (paymentMethod = "stripe") => {
    const baseUrl = window.location.origin;
    const payload = {
      eventId: event.event_id,
      items: selectedItems,
      packItems: selectedPackItems.length > 0 ? selectedPackItems : undefined,
      successUrl: `${baseUrl}/tickets/confirmation?reservation_id={RESERVATION_ID}`,
      cancelUrl: `${baseUrl}/events/${eventSlug}`,
      promoCode: appliedPromo?.code || undefined,
      paymentMethod,
    };
    if (checkoutMode === "account" && customerToken) {
      payload.customerToken = customerToken;
    } else {
      payload.guestEmail = guestEmail.trim();
      payload.guestName = guestName.trim();
      if (guestPhone.trim()) payload.guestPhone = guestPhone.trim();
    }
    return payload;
  };

  const buildTikTokContents = () =>
    selectedItems.map((item) => {
      const tier = tiers.find((t) => t.tier_id === item.tier_id);
      return {
        content_id: String(event.event_id),
        content_name: tier ? `${event.title} – ${tier.name}` : event.title,
        quantity: item.quantity,
        price: tier ? getMemberEffectivePrice(tier, tiers) / 100 : 0,
      };
    });

  const handleCheckout = async () => {
    if (!canCheckout || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await createReservation(buildPayload("stripe"));

      // TikTok: InitiateCheckout — use reservation_id as event_id to deduplicate with server event
      ttqTrack(
        "InitiateCheckout",
        { value: totalCents / 100, currency: "USD", contents: buildTikTokContents() },
        result.reservation_id,
      );

      // Meta: InitiateCheckout — same reservation_id as event_id to deduplicate with server event
      fbqTrack(
        "InitiateCheckout",
        {
          value: totalCents / 100,
          currency: "USD",
          content_type: "product",
          content_ids: buildTikTokContents().map((c) => c.content_id),
          num_items: buildTikTokContents().reduce((n, c) => n + (c.quantity || 0), 0),
        },
        result.reservation_id,
      );

      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        navigate(`/tickets/confirmation?reservation_id=${result.reservation_id}`);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const handlePayPalClick = async () => {
    if (!canCheckout || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await createReservation(buildPayload("paypal"));
      if (result.paypal_order_id) {
        setPendingPayPal({ reservationId: result.reservation_id, paypalOrderId: result.paypal_order_id });
      } else {
        throw new Error("PayPal order could not be created. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // const handleVenmoClick = async () => {
  //   if (!canCheckout || submitting || !customerToken) return;
  //   setSubmitting(true);
  //   setError("");
  //   try {
  //     const result = await createReservation(buildPayload("venmo"));
  //     if (!result.paypal_order_id) throw new Error("Venmo payment could not be initialized.");
  //     setPendingVenmo({ reservationId: result.reservation_id, paypalOrderId: result.paypal_order_id });
  //   } catch (err) {
  //     setError(err.message || "Something went wrong. Please try again.");
  //   } finally {
  //     setSubmitting(false);
  //   }
  // };

  if (loading && !event) return <LoadingPage />;

  if (error && !event) {
    return (
      <div className="etp-container">
        <div className="etp-error">
          <p>{error}</p>
          <button onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/')} className="etp-back-btn">Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="etp-page">
    <div className="etp-container">
      {/* Header */}
      {event && (
        <div className="etp-header">
          {event.hero_image_url && (
            <img src={event.hero_image_url} alt={event.title} className="etp-hero-img" />
          )}
          <div className="etp-header-info">
            <h1 className="etp-title">{event.title}</h1>
            {event.start_at && (() => {
              const TZ = "America/Chicago";
              const normalize = (ts) => {
                const raw = ts.trim().replace(" ", "T");
                return new Date(/[Zz]$/.test(raw) || /[+\-]\d{2}:?\d{2}$/.test(raw) ? raw : raw + "Z");
              };
              const startDate = normalize(event.start_at);
              const timeOpts = { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ };
              const startTime = startDate.toLocaleTimeString("en-US", timeOpts);
              const endTime = event.end_at
                ? normalize(event.end_at).toLocaleTimeString("en-US", timeOpts)
                : null;
              const timeRange = endTime && endTime !== startTime
                ? `${startTime} – ${endTime}`
                : startTime;
              return (
                <p className="etp-date">
                  {startDate.toLocaleDateString("en-US", {
                    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: TZ,
                  })}
                  {" · "}
                  {timeRange}
                </p>
              );
            })()}
            {event.primary_venue?.venue_name && (
              <p className="etp-venue">{event.primary_venue.venue_name}</p>
            )}
            {(event.sponsor_name || event.sponsor_logo_url) && (
              <div className="etp-sponsor-bar">
                <span className="etp-sponsor-label">Sponsored by</span>
                <span className="etp-sponsor-divider" aria-hidden="true" />
                {event.sponsor_logo_url && (
                  <img
                    src={event.sponsor_logo_url}
                    alt={event.sponsor_name || 'Sponsor'}
                    className="etp-sponsor-logo"
                  />
                )}
                {event.sponsor_name && (
                  <span className="etp-sponsor-name">{event.sponsor_name}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="etp-body">
        {/* Left: tier selection + checkout mode */}
        <div className="etp-left">
          {/* Member note */}
          {isMember && (
            <div className="etp-member-banner">
              <FaStar /> You have access to member-exclusive ticket prices
            </div>
          )}

          {/* Already purchased banner */}
          {existingTickets.length > 0 && (
            <div className="etp-already-purchased">
              <FaTicketAlt />
              <span>
                You already have {existingTickets.length} ticket{existingTickets.length > 1 ? "s" : ""} for this event.{" "}
                <button
                  className="etp-view-tickets-link"
                  onClick={() => navigate(`/my-tickets?event_id=${event.event_id}`)}
                >
                  View my tickets
                </button>
              </span>
            </div>
          )}

          {/* Tier list */}
          <section className="etp-section">
            <h2 className="etp-section-title">
              <FaTicketAlt /> Select Tickets
            </h2>
            {loading ? (
              <p className="etp-loading-tiers">Loading ticket options…</p>
            ) : (
              <TierSelector
                tiers={tiers}
                quantities={quantities}
                onQuantityChange={handleQuantityChange}
                onLoginPrompt={triggerLogin}
                promotions={promotions}
                packs={packs}
                packQuantities={packQuantities}
                onPackQuantityChange={handlePackQuantityChange}
                eventCapacityRemaining={eventCapacityRemaining}
              />
            )}
          </section>

          {/* Checkout mode selection */}
          {totalTickets > 0 && !checkoutMode && (
            <section className="etp-section etp-checkout-mode">
              <h2 className="etp-section-title">How would you like to continue?</h2>
              <div className="etp-mode-cards">
                {viewMode === "resident" && customerToken && (
                  <button
                    className="etp-mode-card etp-mode-card--account"
                    onClick={() => setCheckoutMode("account")}
                  >
                    <FaUser className="etp-mode-icon" />
                    <div>
                      <strong>Use my Block Club account</strong>
                      <span>{authEmail}</span>
                    </div>
                  </button>
                )}
                {viewMode !== "resident" && (
                  <button
                    className="etp-mode-card etp-mode-card--signin"
                    onClick={() => { triggerLogin(); }}
                  >
                    <FaUser className="etp-mode-icon" />
                    <div>
                      <strong>Sign in / Create account</strong>
                      <span>
                        {tiers.some((t) => t.is_member_only)
                          ? "Unlock member pricing + track your tickets"
                          : "Earn perks, track tickets easily"}
                      </span>
                    </div>
                  </button>
                )}
                <button
                  className="etp-mode-card etp-mode-card--guest"
                  onClick={() => setCheckoutMode("guest")}
                >
                  <FaEnvelope className="etp-mode-icon" />
                  <div>
                    <strong>Continue as guest</strong>
                    <span>Tickets sent to your email — no account needed</span>
                  </div>
                </button>
              </div>
            </section>
          )}

          {/* Guest form */}
          {checkoutMode === "guest" && totalTickets > 0 && (
            <section className="etp-section">
              <h2 className="etp-section-title">Your Information</h2>
              <div className="etp-guest-form">
                <div className="etp-field">
                  <label htmlFor="guestName">
                    <FaUser /> Full Name *
                  </label>
                  <input
                    id="guestName"
                    type="text"
                    placeholder="Jane Smith"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    autoComplete="name"
                  />
                </div>
                <div className="etp-field">
                  <label htmlFor="guestEmail">
                    <FaEnvelope /> Email *
                  </label>
                  <input
                    id="guestEmail"
                    type="email"
                    placeholder="jane@example.com"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <span className="etp-field-hint">Your QR tickets will be sent here</span>
                </div>
                <div className="etp-field">
                  <label htmlFor="guestPhone">
                    <FaPhone /> Phone (optional)
                  </label>
                  <input
                    id="guestPhone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    autoComplete="tel"
                  />
                </div>
                <button
                  className="etp-mode-switch-btn"
                  onClick={() => setCheckoutMode(null)}
                >
                  ← Sign in as member
                </button>
              </div>
            </section>
          )}

          {/* Account checkout confirmation */}
          {checkoutMode === "account" && totalTickets > 0 && (
            <section className="etp-section etp-account-confirm">
              <div className="etp-account-info">
                <FaUser className="etp-account-icon" />
                <div>
                  <strong>Checking out as</strong>
                  <span>{authEmail}</span>
                </div>
              </div>
              <button
                className="etp-mode-switch-btn"
                onClick={() => setCheckoutMode(null)}
              >
                ← Change
              </button>
            </section>
          )}
        </div>

        {/* Right: order summary + checkout button */}
        <aside className="etp-right">
          <OrderSummary
            tiers={tiers}
            quantities={quantities}
            promotions={promotions}
            appliedPromo={appliedPromo}
            packs={packs}
            packQuantities={packQuantities}
          />

          {totalTickets > 0 && (
            <PromoCodeInput
              eventId={event?.event_id}
              items={selectedItems}
              customerToken={checkoutMode === "account" ? customerToken : null}
              appliedPromo={appliedPromo}
              onApply={(promo) => setAppliedPromo(promo)}
              onRemove={() => setAppliedPromo(null)}
            />
          )}

          {exceedsEventCapacity && (
            <div className="etp-error-banner">
              Only {eventCapacityRemaining} ticket{eventCapacityRemaining !== 1 ? "s" : ""} remaining for this event. Please reduce your selection.
            </div>
          )}

          {error && <div className="etp-error-banner">{error}</div>}

          <PayPalScriptProvider options={{
            clientId: PAYPAL_CLIENT_ID,
            currency: "USD",
            components: "buttons",
            // enableFunding: "venmo",
            ...(IS_DEV && { buyerCountry: "US" }),
          }}>
            {totalTickets > 0 && checkoutMode && (
              <div className="etp-payment-actions">
                <button
                  className="etp-checkout-btn"
                  onClick={handleCheckout}
                  disabled={!canCheckout || submitting}
                >
                  {submitting ? (
                    "Processing…"
                  ) : isFreeOrder ? (
                    <><FaTicketAlt /> Get Free Tickets</>
                  ) : (
                    <><FaLock /> Pay with Card {formatPrice(totalCents)}</>
                  )}
                </button>

                {!isFreeOrder && (
                  <>
                    <div className="etp-payment-divider">
                      <span>or</span>
                    </div>
                    <button
                      className="etp-checkout-btn etp-checkout-btn--paypal"
                      onClick={handlePayPalClick}
                      disabled={!canCheckout || submitting}
                    >
                      <FaPaypal /> Pay with PayPal
                    </button>
                    {/* Venmo checkout disabled
                    {checkoutMode === "account" && customerToken && (
                      <button
                        className="etp-checkout-btn etp-checkout-btn--venmo"
                        onClick={handleVenmoClick}
                        disabled={!canCheckout || submitting}
                      >
                        <svg viewBox="0 0 43 43" width="18" height="18" fill="currentColor" aria-hidden="true">
                          <path d="M35.6 2C36.8 4 37.3 6.1 37.3 8.8c0 8.3-7.1 19.1-12.9 26.7H11.2L6 3.5l12.4-1.2 2.7 21.5C24 18.5 27.4 10.5 27.4 6c0-2.4-.4-4.1-1-5.4L35.6 2z" />
                        </svg>
                        Pay with Venmo
                      </button>
                    )}
                    */}
                  </>
                )}
              </div>
            )}

            {pendingPayPal && (
              <div className="etp-paypal-panel">
                <div className="etp-paypal-panel-header">
                  <p className="etp-paypal-prompt">Complete your PayPal payment</p>
                  <p className="etp-paypal-subtext">
                    A PayPal window will open. Approve the payment to get your tickets.
                  </p>
                </div>
                <PayPalCheckoutButton
                  reservationId={pendingPayPal.reservationId}
                  paypalOrderId={pendingPayPal.paypalOrderId}
                  onSuccess={(rid) => navigate(`/tickets/confirmation?reservation_id=${rid}`)}
                  onError={(msg) => { if (msg) setError(msg); setPendingPayPal(null); }}
                />
                <button className="etp-paypal-cancel" onClick={() => setPendingPayPal(null)}>
                  ← Cancel and go back
                </button>
              </div>
            )}

            {/* Venmo payment panel disabled
            {pendingVenmo && (
              <div className="etp-paypal-panel">
                <div className="etp-paypal-panel-header">
                  <p className="etp-paypal-prompt">Pay with Venmo</p>
                  <p className="etp-paypal-subtext">
                    Tap the Venmo button below to complete your payment.
                  </p>
                </div>
                <PayPalButtons
                  fundingSource={FUNDING.VENMO}
                  style={{ layout: "vertical", color: "blue", shape: "rect", height: 50 }}
                  createOrder={() => pendingVenmo.paypalOrderId}
                  onApprove={async () => {
                    setSubmitting(true);
                    try {
                      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
                      const res = await fetch(
                        `${apiUrl}/ticketing/reservations/${pendingVenmo.reservationId}/paypal-capture`,
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ order_id: pendingVenmo.paypalOrderId }),
                        }
                      );
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.detail || "Capture failed");
                      navigate(`/tickets/confirmation?reservation_id=${pendingVenmo.reservationId}`);
                    } catch (err) {
                      setError(err.message || "Venmo payment failed. Please try again.");
                      setSubmitting(false);
                    }
                  }}
                  onError={(err) => {
                    setError("Venmo payment failed. Please try again.");
                    console.error("Venmo SDK error:", err);
                  }}
                  onCancel={() => { setPendingVenmo(null); setError(""); }}
                />
                <button className="etp-paypal-cancel" onClick={() => { setPendingVenmo(null); setError(""); }}>
                  ← Cancel and go back
                </button>
              </div>
            )}
            */}
          </PayPalScriptProvider>

          {totalTickets > 0 && !checkoutMode && (
            <p className="etp-select-mode-hint">Select a checkout option above to continue</p>
          )}

          {!isFreeOrder && totalCents > 0 && !pendingPayPal && (
            <p className="etp-stripe-note">
              <FaLock /> Secure payment via Stripe
            </p>
          )}

          {!isFreeOrder && event?.refund_cutoff_days != null && event?.start_at && (() => {
            const raw = event.start_at.trim().replace(" ", "T");
            const hasTimezone = /[Zz]$/.test(raw) || /[+\-]\d{2}:?\d{2}$/.test(raw);
            const utcStr = hasTimezone ? raw : raw + "Z";
            const deadline = new Date(new Date(utcStr).getTime() - event.refund_cutoff_days * 86400 * 1000);
            const deadlineStr = deadline.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "America/Chicago" });
            return (
              <p className="etp-refund-note">
                Refund available until {deadlineStr}
              </p>
            );
          })()}
        </aside>
      </div>
    </div>
    </div>
  );
}
